import { useEffect, useRef, useState } from "react";
import { AppDataGrid, gridExportMatrix } from "../../components/AppDataGrid";
import { ExportMenu } from "../../components/AppToolbar";
import { GridSearch } from "../../components/GridSearch";
import { fuzzyMatch } from "../../utils/fuzzyMatch";
import { api } from "../../api/client";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";
import { useConfirm } from "../../components/confirm";

/**
 * Test Code → Test Parameter Defaults → Component → **+ Add / Edit**
 * (prototype v12 `compDlg`).
 *
 * A component is a NAMED SUBSET of this test's sample parameters, so the dialog
 * is a name plus a tick list. The ticks are the WHOLE set, not a delta: an
 * unticked row means "not in this component", which the server applies as a
 * replace inside one transaction.
 *
 * The lab-wide "Default" component (the one `dbo.Component` row with no
 * TestMethod, which every test's parameters point at) cannot be edited here —
 * the grid hides the action for it and the server refuses it anyway.
 */

export type ComponentRow = {
  oid: string;
  component?: string | null;
  comment?: string | null;
  retireDate?: string | null;
  isShared?: boolean;
  /** The component's MEMBERS: "<Parameter Oid>|<name>", ';'-joined. */
  parameterPairs?: string | null;
};

export type SampleParamOption = { oid: string; parameter?: string | null };

/** ADD: the test's parameters that are NOT yet in this component. Same shared
 *  dialog + grid + search as everywhere else, so it looks like the rest of the
 *  app rather than a one-off picker. */
function AddParametersPicker({
  options,
  onClose,
  onAdd,
}: {
  options: SampleParamOption[];
  onClose: () => void;
  onAdd: (ids: string[]) => void;
}) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const shown = q.trim() ? options.filter((p) => fuzzyMatch(q, p.parameter ?? "")) : options;
  return (
    <AppDialog
      open
      title="Add Parameters"
      width={520}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button className="ca-btn ca-btn-solid" disabled={!sel.size} onClick={() => onAdd([...sel])}>
            OK
          </button>
          <button className="ca-btn ghost" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div data-hotkeys-ignore className="tc-comp-search tc-comp-pick-search">
        <GridSearch value={q} onChange={setQ} placeholder="Search parameters…" />
      </div>
      <div className="tc-comp-list">
        <AppDataGrid<SampleParamOption>
          rows={shown}
          getRowId={(r) => r.oid}
          columns={[{ field: "parameter", header: "Parameter" }]}
          selectable
          selectedIds={sel as Set<string | number>}
          onSelectionChange={(ids) => setSel(new Set([...ids].map(String)))}
          defaultPageSize={20}
          exportFilename="add-parameters"
          emptyText={options.length ? `No parameter matches “${q}”.` : "Every parameter is already in this component."}
        />
      </div>
    </AppDialog>
  );
}

/** Ids are compared as STRINGS, and the same Guid reaches this dialog in two
 *  cases: SQL renders a uniqueidentifier uppercase (the member pairs) while a
 *  real Guid is serialised lowercase (the parameter list). Everything keys on
 *  this, so the case a value happens to arrive in cannot matter. */
const key = (v: unknown) => String(v ?? "").toLowerCase();

/** "<Parameter Oid>|<name>" pairs, ';'-joined, as the components query returns
 *  them. A name may contain '|' in theory, so only the FIRST separator is
 *  treated as one. */
function parsePairs(raw?: string | null): SampleParamOption[] {
  if (!raw) return [];
  return raw
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      const i = s.indexOf("|");
      return i < 0
        ? { oid: key(s), parameter: "" }
        : { oid: key(s.slice(0, i)), parameter: s.slice(i + 1) };
    });
}

export function TestComponentDialog({
  open,
  base,
  row,
  params,
  viewOnly = false,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  /** Read-only record: the membership buttons are hidden, the list still reads. */
  viewOnly?: boolean;
  /** null = + Add. */
  row: ComponentRow | null;
  /** This test's sample parameters, keyed by PARAMETER Oid — what Add offers.
   *  Not keyed by Testparameter row: a parameter has a separate row per
   *  component, so row Oids from this list never match a member's. */
  params: SampleParamOption[];
  onClose: () => void;
  onDone: (created: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [retire, setRetire] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  /** Fuzzy filter over the parameter list (owner request 2026-10-27). */
  const [q, setQ] = useState("");
  /** Ticked MEMBERS — what Remove acts on. Separate from `picked`, which is the
   *  membership itself; ticking a row must not drop it from the component. */
  const [memberSel, setMemberSel] = useState<Set<string>>(new Set());
  /** The "add parameters" picker. */
  const [addOpen, setAddOpen] = useState(false);
  /** The component's members as last read from the server. Seeded from the row
   *  the dialog was opened with, replaced by Refresh. */
  const [stored, setStored] = useState<SampleParamOption[]>([]);
  /* Save reads the FIELD, not just React state. A value the browser fills in
     (autofill, a password manager, anything writing element.value directly)
     never fires React's onChange, so `name` stays "" while the box plainly
     shows text — the reported failure: "WasteWater_Testing" on screen, the red
     required cue on the box, and OK answering "Component is required."
     (owner report 2026-10-27). */
  const nameRef = useRef<HTMLInputElement | null>(null);
  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    if (!open) return;
    setName(row?.component ?? "");
    setComment(row?.comment ?? "");
    setRetire(row?.retireDate ? String(row.retireDate).slice(0, 10) : "");
    setQ("");
    setMemberSel(new Set());
    setAddOpen(false);
    setBusy(false);
    if (!row) {
      setPicked(new Set());
      return;
    }
    // Which parameters are in this component right now. Read from the server
    // rather than passed in, so the ticks reflect what is stored even if the
    // grid behind the dialog is a few seconds stale.
    const seeded = parsePairs(row.parameterPairs);
    setStored(seeded);
    setPicked(new Set(seeded.map((m) => m.oid)));
    return;
  }, [open, row, base]);

  /** This test's parameters with their ids normalised, so they key the same way
   *  as the members. */
  const options: SampleParamOption[] = params.map((p) => ({ ...p, oid: key(p.oid) }));

  /** The component's own parameters — what the grid shows, as G5 does. For a
   *  NEW component there is no row yet, so members are whatever Add has put in
   *  `picked`, resolved against the test's parameter list. */
  const members: SampleParamOption[] = [...picked].map(
    (oid) =>
      stored.find((m) => m.oid === oid) ??
      options.find((p) => p.oid === oid) ?? { oid, parameter: "" },
  );
  /** Everything else on the test: what ADD can still offer. Both sides are
   *  PARAMETER Oids, so a member is genuinely excluded — G5 applies the same
   *  rule on ParameterName (TestParameterViewControllercs.cs:7494-7497). */
  const addable = options.filter((p) => !picked.has(p.oid));

  /* The search narrows the MEMBERS. Matching is the SHARED fuzzyMatch
     (standing rule 8), so "alum" finds Aluminum and "pb" finds Lead (Pb)
     exactly as the command palette and the sidebar search do. */
  const shown = q.trim()
    ? members.filter((p) => fuzzyMatch(q, p.parameter ?? ""))
    : members;

  if (!open) return null;

  const save = async () => {
    // The element wins when it holds something React has not seen.
    const n = (nameRef.current?.value ?? name).trim();
    if (n !== name) setName(n);
    if (!n) {
      notify.warning("Component is required.");
      return;
    }
    if (!(await confirm({ title: "Component", message: "Do you want to Save?" }))) return;
    setBusy(true);
    try {
      const body = {
        name: n,
        comment: comment.trim() || null,
        retireDate: retire || null,
        // dbo.Parameter Oids. The server turns them into this component's own
        // Testparameter rows — it never re-points the Default row.
        parameterOids: [...picked],
      };
      if (row) await api.put(`${base}/test-code/components/${row.oid}`, body);
      else await api.post(`${base}/test-code/components`, body);
      onDone(!row);
      onClose();
    } catch (e: unknown) {
      // The server's own refusals (duplicate name, shared component) carry the
      // sentence written for the person who clicked Save.
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not save the component.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Component"
      width={560}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={busy}>OK</button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      <div className="tc-copy-grid tc-guide-grid">
        <label className="tc-copy-lbl">Component<span className="req">*</span></label>
        <input
          ref={nameRef}
          className={`sd-input${name.trim() ? "" : " sd-reqempty"}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          // An autofilled value arrives without onChange; onBlur pulls it into
          // state so the red cue and the field agree before Save is ever used.
          onBlur={(e) => { if (e.target.value !== name) setName(e.target.value); }}
          autoFocus
        />

        <label className="tc-copy-lbl tc-lbl-plain">Comment</label>
        <input className="sd-input" value={comment} onChange={(e) => setComment(e.target.value)} />

        <label className="tc-copy-lbl tc-lbl-plain">Retire Date</label>
        <input className="sd-input" type="date" value={retire} onChange={(e) => setRetire(e.target.value)} />
      </div>

      {/* Add · Remove · Export to, with the search at the far end of the same
          row. Export only reads, so it stays on a read-only record; Add and
          Remove write and do not. */}
      <div className="sd-child-bar tc-comp-bar">
        {!viewOnly && (
          <>
            <button className="ca-btn" onClick={() => setAddOpen(true)} disabled={!addable.length}>
              <span className="tb-i tb-i-green">＋</span> Add
            </button>
            <button className="ca-btn" onClick={() => {
              setPicked((p) => { const n = new Set(p); for (const id of memberSel) n.delete(id); return n; });
              setMemberSel(new Set());
            }} disabled={!memberSel.size}>
              <span className="tb-i tb-i-red">−</span> Remove
            </button>
          </>
        )}
        <ExportMenu
          filename="component-parameters"
          {...gridExportMatrix(members, [{ field: "parameter", header: "Parameter" }])}
        />
        {params.length > 0 && (
          <div data-hotkeys-ignore className="tc-comp-search tc-comp-search--end">
            <GridSearch value={q} onChange={setQ} placeholder="Search parameters…" />
          </div>
        )}
      </div>

      <div className="tc-comp-list">
        <AppDataGrid<SampleParamOption>
          rows={shown}
          getRowId={(r) => r.oid}
          columns={[{ field: "parameter", header: "Parameter" }]}
          selectable
          selectedIds={memberSel as Set<string | number>}
          onSelectionChange={(ids) => setMemberSel(new Set([...ids].map(String)))}
          defaultPageSize={20}
          /* The FULL header cluster, like every other grid on this form (owner
             request 2026-10-27). The toolbar above also carries the labelled
             "Export to" G5 shows, so this grid deliberately has both: the icon
             is the shared one every grid has, the button is the G5 one. */
          exportFilename="component-parameters"
          emptyText={
            !members.length
              ? "No parameters yet — click Add."
              : `No parameter matches “${q}”.`
          }
        />
      </div>

      {addOpen && (
        <AddParametersPicker
          options={addable}
          onClose={() => setAddOpen(false)}
          onAdd={(ids) => {
            setPicked((p) => new Set([...p, ...ids]));
            setAddOpen(false);
          }}
        />
      )}
    </AppDialog>
  );
}
